import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DateControls, defaultRange, type DateRange } from "../components/DateControls";
import { RankingTable } from "../components/RankingTable";
import { useWorkspace } from "../contexts/WorkspaceContext";
import {
  errorMessage,
  formatDate,
  formatKes,
  formatNumber,
  unwrap,
  validDateRange,
} from "../lib/format";
import type {
  DailyCommissionRecord,
  DailySalesRecord,
  Opt,
} from "../lib/types";

interface PageCursor {
  sales: Opt<string>;
  commissions: Opt<string>;
}

export function DailyPage() {
  const { actor, activeShop, meta } = useWorkspace();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initial = useMemo(() => {
    const from = params.get("from") ?? "";
    const to = params.get("to") ?? "";
    return validDateRange(from, to) ? { from, to } : defaultRange();
  }, []);
  const [range, setRange] = useState<DateRange>(initial);
  const [sales, setSales] = useState<DailySalesRecord[]>([]);
  const [commissions, setCommissions] = useState<DailyCommissionRecord[]>([]);
  const [cursor, setCursor] = useState<PageCursor>({ sales: [], commissions: [] });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(params.get("day") ?? "");

  useEffect(() => {
    const next: Record<string, string> = { from: range.from, to: range.to };
    if (expanded) next.day = expanded;
    setParams(next, { replace: true });
  }, [range, expanded, setParams]);

  const load = useCallback(
    async (append: boolean) => {
      if (!actor) return;
      append ? setLoadingMore(true) : setLoading(true);
      setError("");
      try {
        const filter = {
          fromDate: [range.from] as [string],
          toDate: [range.to] as [string],
          cursor: (append ? cursor.sales : []) as Opt<string>,
          limit: 30n,
        };
        const salesPromise =
          !append || cursor.sales.length > 0
            ? actor.listDailySales(filter)
            : null;
        const commissionPromise =
          meta?.commissionOnSales &&
          (!append || cursor.commissions.length > 0)
          ? actor.listDailyCommissions({
              ...filter,
              cursor: (append ? cursor.commissions : []) as Opt<string>,
            })
          : null;
        const [salesResult, commissionResult] = await Promise.all([
          salesPromise,
          commissionPromise,
        ]);
        if (salesResult && "err" in salesResult) throw salesResult.err;
        if (commissionResult && "err" in commissionResult) throw commissionResult.err;
        if (salesResult && "ok" in salesResult) {
          setSales((current) =>
            append ? [...current, ...salesResult.ok.items] : salesResult.ok.items,
          );
        }
        setCursor({
          sales:
            salesResult && "ok" in salesResult
              ? salesResult.ok.nextCursor
              : [],
          commissions:
            commissionResult && "ok" in commissionResult
              ? commissionResult.ok.nextCursor
              : [],
        });
        if (commissionResult && "ok" in commissionResult) {
          setCommissions((current) => append ? [...current, ...commissionResult.ok.items] : commissionResult.ok.items);
        } else if (!append) {
          setCommissions([]);
        }
      } catch (caught) {
        const denied =
          typeof caught === "object" &&
          caught !== null &&
          ("NotOwner" in caught || "NotAuthorized" in caught || "Anonymous" in caught);
        if (denied) navigate("/not-authorized", { replace: true });
        setError(errorMessage(caught));
        if (!append) {
          setSales([]);
          setCommissions([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [actor, cursor.commissions, cursor.sales, meta?.commissionOnSales, navigate, range.from, range.to],
  );

  useEffect(() => {
    setCursor({ sales: [], commissions: [] });
    void load(false);
    // Cursor intentionally excluded for the first page of a changed range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, meta?.commissionOnSales, range.from, range.to]);

  const commissionByDate = useMemo(
    () => new Map(commissions.map((row) => [row.date, row])),
    [commissions],
  );
  const hasMore = cursor.sales.length > 0 || cursor.commissions.length > 0;

  return (
    <div className="page page-enter">
      <div className="page-title-row">
        <div>
          <p className="eyebrow">Daily detail</p>
          <h1>{activeShop?.name}</h1>
        </div>
        <Link className="secondary-button" to={`/shops/${activeShop?.id.toText()}/overview?from=${range.from}&to=${range.to}`}>
          ← Overview
        </Link>
      </div>
      <DateControls value={range} onChange={(next) => { setExpanded(""); setRange(next); }} disabled={loading} />
      {loading ? <div className="daily-loading">Loading daily records…</div> : null}
      {!loading && error ? <div className="error-panel" role="alert"><strong>Daily detail unavailable</strong><p>{error}</p><button className="text-button" onClick={() => void load(false)}>Retry</button></div> : null}
      {!loading && !error && sales.length === 0 ? <div className="chart-empty">No sales were recorded in this period.</div> : null}
      {!loading && sales.length > 0 ? (
        <section className="daily-list" aria-label="Daily sales">
          {sales.map((day) => {
            const commission = commissionByDate.get(day.date);
            const isOpen = expanded === day.date;
            return (
              <article className={isOpen ? "daily-row open" : "daily-row"} key={day.date}>
                <button className="daily-summary" type="button" onClick={() => setExpanded(isOpen ? "" : day.date)} aria-expanded={isOpen}>
                  <span><strong>{formatDate(day.date, true)}</strong><small>{formatNumber(day.totalSales)} transactions</small></span>
                  <span><strong>{formatKes(day.totalSalesKes)}</strong><small>{unwrap(day.marginKes) !== undefined ? `${formatKes(unwrap(day.marginKes) ?? 0n)} margin` : "Margin unavailable"}</small></span>
                  <span aria-hidden="true">{isOpen ? "−" : "+"}</span>
                </button>
                {isOpen ? <DailyBreakdown day={day} commission={commission} /> : null}
              </article>
            );
          })}
        </section>
      ) : null}
      {hasMore ? <button className="load-more" type="button" disabled={loadingMore} onClick={() => void load(true)}>{loadingMore ? "Loading…" : "Load older days"}</button> : null}
    </div>
  );
}

function DailyBreakdown({ day, commission }: { day: DailySalesRecord; commission?: DailyCommissionRecord }) {
  return (
    <div className="daily-breakdown">
      <div className="daily-metrics">
        <span><small>M-Pesa</small><strong>{formatKes(day.totalMpesaKes)}</strong></span>
        <span><small>Cash</small><strong>{formatKes(day.totalCashKes)}</strong></span>
        <span><small>Card</small><strong>{formatKes(day.totalCardKes)}</strong></span>
        <span><small>Discounts</small><strong>{formatKes(day.totalDiscountKes)}</strong></span>
        {unwrap(day.marginPct) !== undefined ? <span><small>FIFO margin</small><strong>{formatNumber(unwrap(day.marginPct) ?? 0n)}%</strong></span> : null}
      </div>
      <div className="daily-columns">
        <RankingTable title="Cashiers" rows={[...day.cashiers].sort((a, b) => Number(b.totalSalesKes - a.totalSalesKes)).map((row) => ({
          key: row.cashier.toText(),
          label: unwrap(row.cashierName) ?? shortPrincipal(row.cashier.toText()),
          value: formatKes(row.totalSalesKes),
          secondary: `${formatNumber(row.totalSales)} sales`,
        }))} />
        {commission ? <RankingTable title={`Commissions · ${formatKes(commission.totalCommissionKes)}`} rows={[...commission.stewards].sort((a, b) => Number(b.commissionKes - a.commissionKes)).map((row) => ({
          key: row.stewardId.toString(),
          label: unwrap(row.stewardName) ?? `Steward #${row.stewardId}`,
          value: formatKes(row.commissionKes),
          secondary: `${formatNumber(row.totalSales)} sales`,
        }))} /> : null}
      </div>
    </div>
  );
}

function shortPrincipal(value: string): string {
  return value.length > 22 ? `${value.slice(0, 9)}…${value.slice(-7)}` : value;
}
